import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Filter format with operator support
export type RuleOperator = "=" | "<>" | ">" | "<" | ">=" | "<=" | "~~" | "~~*" | "in" | "is" | "len>" | "len<" | "len>=" | "len<=" | "len=";

export interface RuleFilter {
  field: string;
  operator: RuleOperator;
  value: string;
}

export interface TagAutoRule {
  id: string;
  tag_id: string;
  filters: RuleFilter[];
  is_active: boolean;
  remove_when_no_match: boolean;
  last_synced_at: string | null;
  users_matched: number;
  created_at: string;
  created_by: string | null;
  updated_at: string;
}

export function useTagAutoRules(tagId?: string) {
  const queryClient = useQueryClient();

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["tag-auto-rules", tagId],
    queryFn: async () => {
      let query = supabase
        .from("tag_auto_rules")
        .select("*")
        .order("created_at", { ascending: false });

      if (tagId) {
        query = query.eq("tag_id", tagId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []).map((rule) => ({
        ...rule,
        filters: (Array.isArray(rule.filters) ? rule.filters : []) as unknown as RuleFilter[],
      })) as TagAutoRule[];
    },
    enabled: tagId !== undefined,
  });

  const createRule = useMutation({
    mutationFn: async ({
      tagId,
      filters,
    }: {
      tagId: string;
      filters: RuleFilter[];
    }) => {
      const { data, error } = await supabase.functions.invoke(
        "admin-user-actions",
        {
          body: {
            action: "create_tag_rule",
            data: {
              tag_id: tagId,
              filters,
            },
          },
        }
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      // Auto-sync the newly created rule
      if (data?.rule?.id) {
        const { data: syncData, error: syncError } = await supabase.functions.invoke(
          "admin-user-actions",
          {
            body: {
              action: "sync_tag_rule",
              data: {
                rule_id: data.rule.id,
              },
            },
          }
        );
        if (syncError) console.error("Auto-sync failed:", syncError);
        if (syncData?.error) console.error("Auto-sync failed:", syncData.error);
      }
      
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate the specific tag's rules query
      queryClient.invalidateQueries({ queryKey: ["tag-auto-rules", variables.tagId] });
      queryClient.invalidateQueries({ queryKey: ["tag-auto-rules"] });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["user-tags"] });
      toast.success("Rule created and synced");
    },
    onError: (error) => {
      toast.error(`Failed to create rule: ${error.message}`);
    },
  });

  const updateRule = useMutation({
    mutationFn: async ({
      ruleId,
      filters,
      isActive,
    }: {
      ruleId: string;
      filters?: RuleFilter[];
      isActive?: boolean;
    }) => {
      const { data, error } = await supabase.functions.invoke(
        "admin-user-actions",
        {
          body: {
            action: "update_tag_rule",
            data: {
              rule_id: ruleId,
              filters,
              is_active: isActive,
            },
          },
        }
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tag-auto-rules"] });
      toast.success("Rule updated successfully");
    },
    onError: (error) => {
      toast.error(`Failed to update rule: ${error.message}`);
    },
  });

  const deleteRule = useMutation({
    mutationFn: async (ruleId: string) => {
      const { data, error } = await supabase.functions.invoke(
        "admin-user-actions",
        {
          body: {
            action: "delete_tag_rule",
            data: {
              rule_id: ruleId,
            },
          },
        }
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tag-auto-rules"] });
      toast.success("Rule deleted successfully");
    },
    onError: (error) => {
      toast.error(`Failed to delete rule: ${error.message}`);
    },
  });

  const syncRule = useMutation({
    mutationFn: async (ruleId: string) => {
      const { data, error } = await supabase.functions.invoke(
        "admin-user-actions",
        {
          body: {
            action: "sync_tag_rule",
            data: {
              rule_id: ruleId,
            },
          },
        }
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["tag-auto-rules"] });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["user-tags"] });
      toast.success(`Synced ${data?.users_matched || 0} users`);
    },
    onError: (error) => {
      toast.error(`Failed to sync rule: ${error.message}`);
    },
  });

  const syncAllRules = useMutation({
    mutationFn: async (tagId: string) => {
      const { data, error } = await supabase.functions.invoke(
        "admin-user-actions",
        {
          body: {
            action: "sync_all_tag_rules",
            data: {
              tag_id: tagId,
            },
          },
        }
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["tag-auto-rules"] });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["user-tags"] });
      toast.success(`Synced all rules: ${data?.total_matched || 0} users matched`);
    },
    onError: (error) => {
      toast.error(`Failed to sync rules: ${error.message}`);
    },
  });

  return {
    rules,
    isLoading,
    createRule: createRule.mutateAsync,
    updateRule: updateRule.mutateAsync,
    deleteRule: deleteRule.mutateAsync,
    syncRule: syncRule.mutateAsync,
    syncAllRules: syncAllRules.mutateAsync,
    isCreating: createRule.isPending,
    isUpdating: updateRule.isPending,
    isDeleting: deleteRule.isPending,
    isSyncing: syncRule.isPending || syncAllRules.isPending,
  };
}
